import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Globe2, Info, Loader2, Plus, RefreshCw, Replace, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg, useApi } from "@/lib/api";
import DomainCard from "./DomainCard";
import DomainWizard from "./DomainWizard";
import { needsPolling } from "./helpers";

const DONE = { check: "Controllo completato", retry: "Nuova richiesta del certificato inviata", primary: "Dominio principale aggiornato", disconnect: "Dominio scollegato" };

function Notice({ tone = "slate", icon: Icon = Info, children, testid }) {
  const cls = { slate: "bg-slate-50 text-slate-700", amber: "bg-amber-50 text-amber-900", sky: "bg-sky-50 text-sky-900" }[tone];
  return <p className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${cls}`} data-testid={testid}><Icon size={15} className="mt-0.5 shrink-0" /><span>{children}</span></p>;
}

function Prerequisite({ data, site }) {
  if (!data.mode) return <Notice tone="amber" icon={TriangleAlert} testid="domains-prereq-hosting">Configura prima l'hosting esterno in <Link to="/hosting" className="font-medium underline">Hosting dei siti</Link>.</Notice>;
  if (!data.edge_enabled) return <Notice tone="amber" icon={TriangleAlert} testid="domains-prereq-edge">Attiva prima «Pubblica su «{site.hosting.available_label}»» qui sopra: il dominio mostra la versione online pubblicata sull'hosting esterno.</Notice>;
  if (!data.ready) return <Notice tone="amber" icon={TriangleAlert} testid="domains-prereq-saas">Completa prima «Domini dei clienti» in <Link to="/hosting" className="font-medium underline">Hosting dei siti</Link> (una sola volta per tutti i siti).</Notice>;
  return null;
}

export default function DomainPanel({ site, onChanged }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wizard, setWizard] = useState(null);
  const changed = useRef(onChanged);
  changed.current = onChanged;
  const sig = useRef(null);
  const apply = useCallback((res) => {
    setData(res);
    const s = res.domains.map((d) => `${d.host}:${d.serving}:${d.role}`).join();
    if (sig.current !== null && s !== sig.current) changed.current();
    sig.current = s;
  }, []);
  const load = useCallback(async () => {
    try { const { data: res } = await api.get(`/sites/${site.id}/domains`, { params: { refresh: true } }); apply(res); } catch (e) { toast.error(errMsg(e)); setData(false); }
  }, [api, site.id, apply, site.hosting?.edge_enabled, site.hosting?.mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!needsPolling(data)) return undefined; const t = setInterval(load, 20000); return () => clearInterval(t); }, [data, load]);
  const act = async (kind, d) => {
    setBusy(true);
    try {
      const base = `/sites/${site.id}/domains`;
      const { data: res } = await (kind === "check" ? api.post(`${base}/check`) : kind === "disconnect" ? api.delete(`${base}/${d.id}`) : api.post(`${base}/${d.id}/${kind}`));
      apply(res); toast.success(DONE[kind]);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (data === false) return null;
  if (!data) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" />Caricamento domini…</div>;
  const has = data.domains.length > 0;
  const usable = data.mode && data.edge_enabled && data.ready;
  const prim = data.domains.find((d) => d.role === "primary");
  return (
    <section className="max-w-4xl space-y-4 border-t border-slate-100 pt-6" data-testid="domain-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="inline-flex items-center gap-2 text-base font-semibold text-slate-900"><Globe2 size={17} className="text-sky-600" />Dominio personalizzato</h3>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Il sito online sul dominio del cliente (es. www.cliente.it) con HTTPS automatico. Anteprima e versioni restano su {data.platform_domain || "la piattaforma"}.</p>
        </div>
        {data.mode && <span className="whitespace-nowrap rounded-full border border-slate-200 px-2.5 py-1 text-[11px] text-slate-600" data-testid="domains-quota">{data.quota.used} / {data.quota.included} domini inclusi usati</span>}
      </div>
      <Prerequisite data={data} site={site} />
      {has && !data.live_published && <Notice tone="amber" icon={TriangleAlert} testid="domains-not-published">Il sito non ha ancora una versione online su questo hosting: usa «Pubblica online», altrimenti il dominio non mostra nulla.</Notice>}
      {data.needs_rebuild && <Notice tone="sky" testid="domains-needs-rebuild">Il dominio è attivo: ricompila e pubblica il sito per aggiornare canonical e sitemap al nuovo indirizzo.</Notice>}
      {prim?.serving && <Notice testid="domains-redirect-note">L'indirizzo {site.hosting.live_host || `${site.slug}.${data.platform_domain}`} e le varianti rimandano a {prim.host} con un 301 (nessun contenuto duplicato).</Notice>}
      {has && data.server_only && <Notice testid="domains-emulator-note">Emulatore: il dominio risponde solo dal server (usa «Prova dal server» nel riquadro Online).</Notice>}
      {data.domains.map((d) => <DomainCard key={d.id} d={d} target={data.cname_target} busy={busy} onAction={act} />)}
      {usable && (
        <div className="flex flex-wrap gap-2">
          {!has && <Button onClick={() => setWizard("new")} className="bg-sky-600 hover:bg-sky-700" data-testid="domains-connect-btn"><Plus size={14} className="mr-1.5" />Collega un dominio</Button>}
          {has && <Button variant="outline" disabled={busy} onClick={() => act("check")} data-testid="domains-check-btn">{busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCw size={14} className="mr-1.5" />}Verifica ora</Button>}
          {has && <Button variant="ghost" disabled={busy} onClick={() => setWizard("replace")} data-testid="domains-replace-btn"><Replace size={14} className="mr-1.5" />Sostituisci dominio</Button>}
        </div>
      )}
      <DomainWizard open={!!wizard} onOpenChange={(o) => !o && setWizard(null)} site={site} data={data} replacing={wizard === "replace"} onData={apply} />
    </section>
  );
}
